(() => {
  class PurchaseEligibilityService {
    constructor() {
      this.apiBaseUrl = window.theme?.internalApp?.baseUrl;
      this.batchCache = new Map();
      this.batchCacheTTL = 5 * 60 * 1000;
      this.storage = typeof localStorage !== 'undefined' ? localStorage : null;
      this.CACHE_KEY = 'asp_purchase_eligibility_cache';
      this.PRODUCTS_CACHE_KEY = 'asp_entitlements_cache';
      this.lastValidation = new Map();

      window.addEventListener('cart:item-added', () => {
        this.clearCache();
      });
    }

    rememberValidation(validationData) {
      if (!Array.isArray(validationData)) {
        return;
      }

      for (const item of validationData) {
        if (item && item.variantId !== undefined && item.variantId !== null) {
          this.lastValidation.set(String(item.variantId), item);
        }
      }
    }

    getKnownValidation(variantIds = null) {
      if (!variantIds) {
        return [...this.lastValidation.values()];
      }

      return variantIds.map(variantId => this.lastValidation.get(String(variantId))).filter(Boolean);
    }

    resolveDiscountedCents(item, originalCents) {
      const discountAmountCents = Number(item?.discountAmountCents);

      if (!Number.isFinite(discountAmountCents) || discountAmountCents <= 0) {
        return null;
      }

      const discountedCents = Math.round(originalCents - discountAmountCents);

      return discountedCents > 0 ? discountedCents : null;
    }

    formatMoneyCents(cents) {
      if (window.currencies_default && typeof window.currencies_default.formatMoney === 'function') {
        return window.currencies_default.formatMoney(cents);
      }

      return `$${(cents / 100).toFixed(2)}`;
    }

    getCustomerId() {
      const customerScript = document.querySelector('.check-customer');

      if (!customerScript) {
        return null;
      }

      try {
        const customerData = JSON.parse(customerScript.textContent);

        return customerData.externalId || null;
      } catch {
        return null;
      }
    }

    getCacheKey(variantIds, customerId) {
      const sortedIds = Array.isArray(variantIds) ? variantIds.sort().join(',') : String(variantIds);
      const cacheIdentifier = customerId ? `${sortedIds}:${customerId}` : sortedIds;

      return `${this.CACHE_KEY}:${cacheIdentifier}`;
    }

    getCachedValidation(variantIds, customerId) {
      if (!this.storage) {
        return null;
      }

      try {
        const cacheKey = this.getCacheKey(variantIds, customerId);
        const cached = this.storage.getItem(cacheKey);

        if (!cached) {
          return null;
        }

        const data = JSON.parse(cached);

        if (Date.now() - data.timestamp > this.batchCacheTTL) {
          this.storage.removeItem(cacheKey);

          return null;
        }

        return data.value;
      } catch (error) {
        console.error('[PurchaseEligibilityService] Error reading cache:', error);

        return null;
      }
    }

    setCachedValidation(variantIds, validationData, customerId) {
      if (!this.storage) {
        return;
      }

      try {
        const cacheKey = this.getCacheKey(variantIds, customerId);

        this.storage.setItem(
          cacheKey,
          JSON.stringify({
            timestamp: Date.now(),
            value: validationData,
          })
        );
      } catch (error) {
        console.error('[PurchaseEligibilityService] Error writing cache:', error);
      }
    }

    clearCache() {
      if (!this.storage) {
        return;
      }

      try {
        const keysToRemove = [];

        for (let i = 0; i < this.storage.length; i++) {
          const key = this.storage.key(i);

          if (key && key.startsWith(this.CACHE_KEY)) {
            keysToRemove.push(key);
          }
        }

        keysToRemove.forEach(key => this.storage.removeItem(key));
        this.storage.removeItem(this.PRODUCTS_CACHE_KEY);
      } catch (error) {
        console.error('[PurchaseEligibilityService] Error clearing cache:', error);
      }
    }

    async validateSingle(variantId, customerId = null) {
      const wrappedId = Array.isArray(variantId) ? variantId[0] : variantId;
      const customIdToUse = customerId || this.getCustomerId();

      const cached = this.getCachedValidation([wrappedId], customIdToUse);

      if (cached && cached.length > 0) {
        return cached[0];
      }

      try {
        const result = await this.validateBatch([wrappedId], customIdToUse);

        if (Array.isArray(result) && result.length > 0) {
          const validationData = result[0];

          if (validationData) {
            return validationData;
          }
        }

        return { variantId: wrappedId, error: 'No data returned', eligible: false };
      } catch (error) {
        return { variantId: wrappedId, error: error.message, eligible: false };
      }
    }

    async validateBatch(variantIds = [], customerId = null) {
      if (!Array.isArray(variantIds) || variantIds.length === 0) {
        return [];
      }

      const customIdToUse = customerId || this.getCustomerId();

      try {
        const queryParams = new URLSearchParams();
        const variantsParam = variantIds.join(',');

        queryParams.set('variants', variantsParam);

        if (customIdToUse) {
          queryParams.set('customerId', customIdToUse);
        }

        const apiUrl = `${this.apiBaseUrl}/product-variants/purchase-eligibility?${queryParams}`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => '');

          console.warn('[PurchaseEligibilityService] HTTP error response:', {
            status: response.status,
            statusText: response.statusText,
            body: errorText,
          });

          return variantIds.map(variantId => ({
            variantId: String(variantId),
            error: `HTTP ${response.status}: ${response.statusText}`,
            eligible: false,
          }));
        }

        const data = await response.json();

        if (!Array.isArray(data)) {
          return variantIds.map(variantId => ({
            variantId: String(variantId),
            error: 'Invalid response format',
            eligible: false,
          }));
        }

        this.setCachedValidation(variantIds, data, customIdToUse);
        this.rememberValidation(data);
        this.setDiscountLLPOD(data);

        return data;
      } catch (error) {
        return variantIds.map(variantId => ({
          variantId: String(variantId),
          error: error.message,
          eligible: false,
        }));
      }
    }

    setDiscountLLPOD(validationData) {
      const filterLLPOD = validationData.filter(data => data.isDiscountedProduct);

      for (const item of filterLLPOD) {
        const svgElements = document.querySelectorAll(`.svg-product-${item.variantId}`);

        svgElements.forEach(svgEl => {
          const container = svgEl.closest('.swt-item');

          if (!container) {
            return;
          }

          const priceWrapper = container.querySelector('.swt-option-variant-price');

          if (!priceWrapper) {
            return;
          }

          const originalPriceSource =
            priceWrapper.querySelector('.price-origin') || priceWrapper.querySelector('price-component');

          const originalPriceEl = parseFloat(originalPriceSource?.dataset.value || priceWrapper.dataset.variantPrice);

          if (!originalPriceEl) {
            return;
          }

          const originalCents = originalPriceEl;
          const discountedCents = this.resolveDiscountedCents(item, originalCents);

          if (discountedCents === null) {
            return;
          }

          const discountedFormatted = this.formatMoneyCents(discountedCents);
          const originalFormatted = this.formatMoneyCents(originalCents);

          priceWrapper.innerHTML = `
            <price-component class='price-origin price-discount-origin' data-value='${originalCents}' style='text-decoration:line-through'>${originalFormatted}</price-component>
            <price-component class='price-final price-discount-final' data-value='${discountedCents}' style='color:#bf2d2e!important'>${discountedFormatted}</price-component>
          `;

          container.classList.add('swt-item--discounted');
        });
      }
    }
  }

  window.PurchaseEligibilityService = PurchaseEligibilityService;

  if (!window.purchaseEligibilityService) {
    window.purchaseEligibilityService = new PurchaseEligibilityService();
  }
})();
